import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import {
  Alert,
  alpha,
  Box,
  CircularProgress,
  Grid,
  Paper,
  Typography,
  useTheme,
} from "@mui/material";

// CUSTOM COMPONENTS
import { DeliveryServiceStats } from "./DeliveryServiceStats";
import { LateOrdersStats } from "./LateOrdersStats";
import { DeliveryTeamStats } from "./DeliveryTeamStats";
import { OrderDistributionChart } from "./OrderDistributionChart";
import { getFakeDeliveryData } from "./fakeData";
import { DriverPerformanceStats } from "./DriverPerformanceStats";
import { DeliveryByRadiusStats } from "./DeliveryByRadiusStats";

// API SERVICES
// import { getDeliveryStats } from "../../../../api/report/deliveryStatisticsReport";

// ANIMATION VARIANTS
const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    y: -20,
    opacity: 0,
    transition: {
      duration: 0.2,
    },
  },
};

// MAIN COMPONENT
export default function DeliveryOrdersReportView({ startDate, endDate }) {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({
    driversData: [],
    serviceData: [],
    lateOrdersData: null,
    teamStatsData: null,
    deliveryTrendData: [],
    orderDistributionData: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Option 1: Fetch all data from a combined endpoint
        // const response = await getDeliveryStats(startDate, endDate);
        // setData(response.data);

        await new Promise((resolve) => setTimeout(resolve, 1000));

        // Get fake data
        const fakeData = getFakeDeliveryData();
        setData(fakeData);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching delivery stats:", error);
        setError("Failed to load delivery statistics. Please try again later.");
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
      }}
    >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            Relatório de Entregas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Análise detalhada do desempenho das entregas no período selecionado
          </Typography>
        </Box>

        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Driver Performance Stats */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <DriverPerformanceStats startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>

          {/* Delivery Service Stats */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <DeliveryServiceStats startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>

          {/* Late Orders Stats */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              {data.lateOrdersData ? (
                <LateOrdersStats startDate={startDate} endDate={endDate} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Delivery Team Stats */}
          <Grid
            item
            xs={12}
            md={6}
            component={motion.div}
            variants={itemVariants}
          >
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              {data.teamStatsData ? (
                <DeliveryTeamStats startDate={startDate} endDate={endDate} />
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: 300,
                  }}
                >
                  <CircularProgress />
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Delivery By Radius Stats */}
          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <DeliveryByRadiusStats startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>

          {/* Order Distribution Chart */}
          <Grid item xs={12} component={motion.div} variants={itemVariants}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                height: "100%",
                backgroundColor: alpha(theme.palette.background.paper, 0.8),
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  boxShadow: "0 5px 15px rgba(0,0,0,0.08)",
                  transform: "translateY(-4px)",
                },
              }}
            >
              <OrderDistributionChart startDate={startDate} endDate={endDate} />
            </Paper>
          </Grid>
        </Grid>
      </motion.div>
    </Box>
  );
}
